import { createSelector } from 'reselect';
// Import state types.
import {
	AppState,
} from '@app/redux/states';
// Import selectors.
import {
	makeAreAllEndorsementBuildingEntityFieldsDisallowedSelector,
	makeAreAllEndorsementDwellingDetailsEntityFieldsDisallowedSelector,
	makeAreAllEndorsementDwellingLimitEntityFieldsDisallowedSelector,
	makeAreAllEndorsementDwellingPropertyAddressEntityFieldsDisallowedSelector,
	makeAreAllEndorsementInsuredInformationEntityFieldsDisallowedSelector,
	makeAreAllEndorsementMortgageeEntityFieldsDisallowedSelector,
	makeAreAllEndorsementPerilEntityFieldsDisallowedSelector,
	makeAreOneOrMoreEndorsementBuildingEntityFieldsDisallowedSelector,
	makeAreOneOrMoreEndorsementMortgageeEntityFieldsDisallowedSelector,
	makeCanEndorsementAddBuildingsSelector,
	makeGetEndorsementNewBuildingGuidsSelector,
	makeGetEndorsementNewMortgageeGuidsSelector,
	makeGetEndorsementSubmissionStatusErrorsSelector,
	makeIsEndorsementDateValidSelector,
	makeIsEndorsementInTheWorksSelector,
} from '@app/redux/selectors';
// Import props.
import {
	OptionalSectionKeyProps,
	SectionProps,
} from '@app/props';
// Import api models.
import {
	EndorsementSection,
	EndorsementSubmissionStatusError,
	SectionStatusInfo,
} from '@app/models/api';
import {
	EndorsementEntityField,
	EndorsementEntityType,
	EndorsementSectionName,
	EndorsementSubmissionStatusReason,
    SectionStatus
} from '@app/enumerations';

interface ICreateEndorsementParameters {
	hasErrors: boolean;
	isEditable: boolean;
	friendlyName: string;
	sectionName: EndorsementSectionName;
	sectionKey?: string;
	errorMessages?: string[];
};

// Utility method for creating endorsement sections.
const createEndorsementSection = (params: ICreateEndorsementParameters): EndorsementSection => {
	// Setup the section status info.
	let endorsementSectionStatusInfo: SectionStatusInfo = new SectionStatusInfo();
	endorsementSectionStatusInfo.statusCode = SectionStatus.COMPLETE;
	endorsementSectionStatusInfo.statusMessage = String.empty();
	// If there are any error, then mark the section as required.
	if (params.hasErrors && params.isEditable) {
		let statusMessage: string = params.errorMessages?.filter(errorMessage => !String.isNullOrWhiteSpace(errorMessage))
			.map(errorMessage => errorMessage.replaceAllWithPattern(/\.$/gm, '') ?? String.empty())
			.join('.  ')
			?? String.empty();
		endorsementSectionStatusInfo.statusCode = SectionStatus.REQUIRED;
		endorsementSectionStatusInfo.statusMessage = !String.isNullOrWhiteSpace(statusMessage)
			? `Before proceeding forward with your endorsement, please resolve the following issues: ${statusMessage}`
			: 'One or more questions require your attention.  Please review and correct your answers.';
	}
	// Set up the section data.
	let section: EndorsementSection = new EndorsementSection();
	section.friendlyName = params.friendlyName;
	section.isEditable = params.isEditable;
	section.key = params.sectionKey ?? null;
	section.name = params.sectionName;
	section.status = endorsementSectionStatusInfo;

	return section;
}

// Fetches the endorsement section name.
const getEndorsementSectionName = (state: AppState, props: SectionProps<EndorsementSectionName>): EndorsementSectionName => {
	return props.sectionName;
}

// Fetches the optional section key prop.
const getOptionalEndorsementSectionKey = (state: AppState, props: OptionalSectionKeyProps): string | undefined => {
	return props.sectionKey;
}

// Get Endorsement Building Sections Selector.
const makeGetEndorsementBuildingSectionsSelector = () => createSelector(
	[
		makeIsEndorsementInTheWorksSelector(),
		makeGetEndorsementNewBuildingGuidsSelector(),
		makeGetEndorsementSubmissionStatusErrorsSelector(),
		makeAreAllEndorsementBuildingEntityFieldsDisallowedSelector(),
	],
	(
		isEndorsementInTheWorks: boolean,
		newBuildingGuids: string[],
		endorsementSubmissionStatusErrors: EndorsementSubmissionStatusError[],
		areAllEndorsementBuildingEntityFieldsDisallowed: boolean,
	): EndorsementSection[] => {
		return newBuildingGuids.map((newBuildingGuid: string) => {
			// Check to see if any of the building data is invalid or missing.
			let inputErrors: EndorsementSubmissionStatusError[] = [];
			if (endorsementSubmissionStatusErrors.length > 0) {
				inputErrors = endorsementSubmissionStatusErrors.filter((statusError: EndorsementSubmissionStatusError) => {
					return (statusError.reason === EndorsementSubmissionStatusReason.MISSING_DATA || statusError.reason === EndorsementSubmissionStatusReason.INVALID_DATA)
						&& statusError.entityType === EndorsementEntityType.BUILDING 
						&& statusError.entityId?.toLowerCase() === newBuildingGuid.toLowerCase();
				});
			}

			let hasErrors: boolean = inputErrors.length > 0;
			// Create section.
			return createEndorsementSection({
				hasErrors: hasErrors,
				isEditable: isEndorsementInTheWorks
					&& !areAllEndorsementBuildingEntityFieldsDisallowed,
				friendlyName: 'Building',
				sectionName: EndorsementSectionName.BUILDING,
				sectionKey: newBuildingGuid,
				errorMessages: inputErrors.map(inputError => inputError.message ?? String.empty()),
			});
		});
	}
);

// Get Endorsement Mortgagee Sections Selector.
const makeGetEndorsementMortgageeSectionsSelector = () => createSelector(
	[
		makeIsEndorsementInTheWorksSelector(),
		makeGetEndorsementNewMortgageeGuidsSelector(),
		makeGetEndorsementSubmissionStatusErrorsSelector(),
		makeAreAllEndorsementMortgageeEntityFieldsDisallowedSelector(),
	],
	(
		isEndorsementInTheWorks: boolean,
		newMortgageeGuids: string[],
		endorsementSubmissionStatusErrors: EndorsementSubmissionStatusError[],
		areAllEndorsementMortgageeEntityFieldsDisallowed: boolean,
	): EndorsementSection[] => {
		return newMortgageeGuids.map((newMortgageeGuid: string) => {
			// Check to see if any of the mortgagee data is invalid or missing.
			let inputErrors: EndorsementSubmissionStatusError[] = [];
			if (endorsementSubmissionStatusErrors.length > 0) {
				inputErrors = endorsementSubmissionStatusErrors.filter((statusError: EndorsementSubmissionStatusError) => {
					return (statusError.reason === EndorsementSubmissionStatusReason.MISSING_DATA || statusError.reason === EndorsementSubmissionStatusReason.INVALID_DATA)
						&& statusError.entityType === EndorsementEntityType.MORTGAGEE
						&& statusError.entityId?.toLowerCase() === newMortgageeGuid.toLowerCase();
				});
			}
			let hasErrors: boolean = inputErrors.length > 0;
			// Create section.
			return createEndorsementSection({
				hasErrors: hasErrors,
				isEditable: isEndorsementInTheWorks
					&& !areAllEndorsementMortgageeEntityFieldsDisallowed,
				friendlyName: 'Mortgagee',
				sectionName: EndorsementSectionName.MORTGAGEE,
				sectionKey: newMortgageeGuid,
				errorMessages: inputErrors.map(inputError => inputError.message ?? String.empty()),
			});
		});
	}
);

// Get Endorsement Dwelling Property Address Section Selector.
const makeGetEndorsementDwellingPropertyAddressSectionSelector = () => createSelector(
	[
		makeIsEndorsementInTheWorksSelector(),
		makeGetEndorsementSubmissionStatusErrorsSelector(),
		makeAreAllEndorsementDwellingPropertyAddressEntityFieldsDisallowedSelector(),
	],
	(
		isEndorsementInTheWorks: boolean,
		endorsementSubmissionStatusErrors: EndorsementSubmissionStatusError[],
		areAllEndorsementDwellingPropertyAddressEntityFieldsDisallowed: boolean,
	): EndorsementSection => {
		// Check to see if any of the property address data is invalid or missing.
		let inputErrors: EndorsementSubmissionStatusError[] = [];
		if (endorsementSubmissionStatusErrors.length > 0) {
			inputErrors = endorsementSubmissionStatusErrors.filter((statusError: EndorsementSubmissionStatusError) => {
				return (statusError.reason === EndorsementSubmissionStatusReason.MISSING_DATA || statusError.reason === EndorsementSubmissionStatusReason.INVALID_DATA)
					&& statusError.entityType === EndorsementEntityType.POLICY
					&& EndorsementEntityField.getDwellingPropertyAddressRelatedEntityFields().some(relatedEntityField => relatedEntityField === statusError.fieldAffected);
			});
		}
		let hasErrors: boolean = inputErrors.length > 0;
		// Create section.
		return createEndorsementSection({
			hasErrors: hasErrors,
			isEditable: isEndorsementInTheWorks
				&& !areAllEndorsementDwellingPropertyAddressEntityFieldsDisallowed,
			friendlyName: 'Property Address',
			sectionName: EndorsementSectionName.PROPERTY_ADDRESS,
			errorMessages: inputErrors.map(inputError => inputError.message ?? String.empty()),
		});
	}
);

// Get Endorsement Dwelling Property Details Section Selector.
const makeGetEndorsementDwellingPropertyDetailsSectionSelector = () => createSelector(
	[
		makeIsEndorsementInTheWorksSelector(),
		makeGetEndorsementSubmissionStatusErrorsSelector(),
		makeAreAllEndorsementDwellingDetailsEntityFieldsDisallowedSelector(),
	],
	(
		isEndorsementInTheWorks: boolean,
		endorsementSubmissionStatusErrors: EndorsementSubmissionStatusError[],
		areAllEndorsementDwellingDetailsEntityFieldsDisallowed: boolean,
	): EndorsementSection => {
		// Check to see if any of the dwelling data is invalid or missing.
		let inputErrors: EndorsementSubmissionStatusError[] = [];
		if (endorsementSubmissionStatusErrors.length > 0) {
			inputErrors = endorsementSubmissionStatusErrors.filter((statusError: EndorsementSubmissionStatusError) => {
				return (statusError.reason === EndorsementSubmissionStatusReason.MISSING_DATA || statusError.reason === EndorsementSubmissionStatusReason.INVALID_DATA)
					&& statusError.entityType === EndorsementEntityType.POLICY
					&& EndorsementEntityField.getDwellingPropertyDetailsRelatedEntityFields().some(relatedEntityField => relatedEntityField === statusError.fieldAffected);
			});
		}
		let hasErrors: boolean = inputErrors.length > 0;
		// Create section.
		return createEndorsementSection({
			hasErrors: hasErrors,
			isEditable: isEndorsementInTheWorks
				&& !areAllEndorsementDwellingDetailsEntityFieldsDisallowed,
			friendlyName: 'Dwelling',
			sectionName: EndorsementSectionName.DWELLING,
			errorMessages: inputErrors.map(inputError => inputError.message ?? String.empty()),
		});
	}
);

// Get Endorsement Dwelling Limits Section Selector.
const makeGetEndorsementDwellingLimitsSectionSelector = () => createSelector(
	[
		makeIsEndorsementInTheWorksSelector(),
		makeGetEndorsementSubmissionStatusErrorsSelector(),
		makeAreAllEndorsementDwellingLimitEntityFieldsDisallowedSelector(),
	],
	(
		isEndorsementInTheWorks: boolean,
		endorsementSubmissionStatusErrors: EndorsementSubmissionStatusError[],
		areAllEndorsementDwellingLimitEntityFieldsDisallowed: boolean,
	): EndorsementSection => {
		// Check to see if any of the limits are invalid or missing.
		let inputErrors: EndorsementSubmissionStatusError[] = [];
		if (endorsementSubmissionStatusErrors.length > 0) {
			inputErrors = endorsementSubmissionStatusErrors.filter((statusError: EndorsementSubmissionStatusError) => {
				return (statusError.reason === EndorsementSubmissionStatusReason.MISSING_DATA || statusError.reason === EndorsementSubmissionStatusReason.INVALID_DATA)
					&& statusError.entityType === EndorsementEntityType.POLICY
					&& EndorsementEntityField.getDwellingLimitsRelatedEntityFields().some(relatedEntityField => relatedEntityField === statusError.fieldAffected);
			});
		}
		let hasErrors: boolean = inputErrors.length > 0;
		// Create section.
		return createEndorsementSection({
			hasErrors: hasErrors,
			isEditable: isEndorsementInTheWorks
				&& !areAllEndorsementDwellingLimitEntityFieldsDisallowed,
			friendlyName: 'Limits',
			sectionName: EndorsementSectionName.LIMITS,
			errorMessages: inputErrors.map(inputError => inputError.message ?? String.empty()),
		});
	}
);

// Get Endorsement Perils And Deductibles Section Selector.
const makeGetEndorsementPerilsAndDeductiblesSectionSelector = () => createSelector(
	[
		makeIsEndorsementInTheWorksSelector(),
		makeGetEndorsementSubmissionStatusErrorsSelector(),
		makeAreAllEndorsementPerilEntityFieldsDisallowedSelector(),
	],
	(
		isEndorsementInTheWorks: boolean,
		endorsementSubmissionStatusErrors: EndorsementSubmissionStatusError[],
		areAllEndorsementPerilEntityFieldsDisallowed: boolean,
	): EndorsementSection => {
		// Check to see if any of the perils and deductibles data is invalid or missing.
		let inputErrors: EndorsementSubmissionStatusError[] = [];
		if (endorsementSubmissionStatusErrors.length > 0) {
			inputErrors = endorsementSubmissionStatusErrors.filter((statusError: EndorsementSubmissionStatusError) => {
				return (statusError.reason === EndorsementSubmissionStatusReason.MISSING_DATA || statusError.reason === EndorsementSubmissionStatusReason.INVALID_DATA)
					&& statusError.entityType === EndorsementEntityType.POLICY
					&& EndorsementEntityField.getPerilRelatedEntityFields().some(relatedEntityField => relatedEntityField === statusError.fieldAffected);
			});
		}
		let hasErrors: boolean = inputErrors.length > 0;
		// Create section.
		return createEndorsementSection({
			hasErrors: hasErrors,
			isEditable: isEndorsementInTheWorks
				&& !areAllEndorsementPerilEntityFieldsDisallowed,
			friendlyName: 'Perils And Deductibles',
			sectionName: EndorsementSectionName.PERILS_AND_DEDUCTIBLES,
			errorMessages: inputErrors.map(inputError => inputError.message ?? String.empty()),
		});
	}
);

// Get Endorsement Insured Section Selector.
const makeGetEndorsementInsuredSectionSelector = () => createSelector(
	[
		makeIsEndorsementInTheWorksSelector(),
		makeGetEndorsementSubmissionStatusErrorsSelector(),
		makeAreAllEndorsementInsuredInformationEntityFieldsDisallowedSelector(),
	],
	(
		isEndorsementInTheWorks: boolean,
		endorsementSubmissionStatusErrors: EndorsementSubmissionStatusError[],
		areAllEndorsementInsuredInformationEntityFieldsDisallowed: boolean,
	): EndorsementSection => {
		// Check to see if any of the insured data is invalid or missing.
		let inputErrors: EndorsementSubmissionStatusError[] = [];
		if (endorsementSubmissionStatusErrors.length > 0) {
			inputErrors = endorsementSubmissionStatusErrors.filter((statusError: EndorsementSubmissionStatusError) => {
				return (statusError.reason === EndorsementSubmissionStatusReason.MISSING_DATA || statusError.reason === EndorsementSubmissionStatusReason.INVALID_DATA)
					&& statusError.entityType === EndorsementEntityType.POLICY
					&& EndorsementEntityField.getInsuredRelatedEntityFields().some(relatedEntityField => relatedEntityField === statusError.fieldAffected);
			});
		}
		let hasErrors: boolean = inputErrors.length > 0;
		// Create section.
		return createEndorsementSection({
			hasErrors: hasErrors,
			isEditable: isEndorsementInTheWorks
				&& !areAllEndorsementInsuredInformationEntityFieldsDisallowed,
			friendlyName: 'Insured',
			sectionName: EndorsementSectionName.INSURED,
			errorMessages: inputErrors.map(inputError => inputError.message ?? String.empty()),
		});
	}
);

// Get Endorsement Coverages View Section Selector.
const makeGetEndorsementCoveragesViewSectionSelector = () => createSelector(
	[
		makeGetEndorsementCoveragesRelatedSectionsSelector(),
		makeIsEndorsementInTheWorksSelector(),
	],
	(
		coverageRelatedSections: EndorsementSection[],
		isEndorsementInTheWorks: boolean,
	): EndorsementSection => {
		let doCoverageRelatedSectionsHaveErrors: boolean = false;
		let areAnyCoverageRelatedSectionsEditable: boolean = true;

		if (coverageRelatedSections.length > 0) {
			doCoverageRelatedSectionsHaveErrors = coverageRelatedSections.some((coverageRelatedSection: EndorsementSection) => coverageRelatedSection.status?.statusCode === SectionStatus.REQUIRED);
			areAnyCoverageRelatedSectionsEditable = coverageRelatedSections.some((coverageRelatedSection: EndorsementSection) => coverageRelatedSection.isEditable);
		}

		// Create section.
		return createEndorsementSection({
			hasErrors: doCoverageRelatedSectionsHaveErrors,
			isEditable: areAnyCoverageRelatedSectionsEditable && isEndorsementInTheWorks,
			friendlyName: 'Coverages',
			sectionName: EndorsementSectionName.COVERAGES_VIEW,
			errorMessages: ['One or more questions in the sections below require your attention.  Please review and correct your answers.'],
		});
	}
);

// Get Endorsement Insured View Section Selector.
const makeGetEndorsementInsuredViewSectionSelector = () => createSelector(
	[
		makeGetEndorsementInsuredRelatedSectionsSelector(),
		makeIsEndorsementInTheWorksSelector()
	],
	(
		insuredRelatedSections: EndorsementSection[],
		isEndorsementInTheWorks: boolean
	): EndorsementSection => {
		let doInsuredRelatedSectionsHaveErrors: boolean = false;
		let areAnyInsuredRelatedSectionsEditable: boolean = true;

		if (insuredRelatedSections.length > 0) {
			doInsuredRelatedSectionsHaveErrors = insuredRelatedSections.some((insuredRelatedSection: EndorsementSection) => insuredRelatedSection.status?.statusCode === SectionStatus.REQUIRED);
			areAnyInsuredRelatedSectionsEditable = insuredRelatedSections.some((insuredRelatedSection: EndorsementSection) => insuredRelatedSection.isEditable);
		}

		// Create section.
		return createEndorsementSection({
			hasErrors: doInsuredRelatedSectionsHaveErrors,
			isEditable: areAnyInsuredRelatedSectionsEditable && isEndorsementInTheWorks,
			friendlyName: 'Insured Information',
			sectionName: EndorsementSectionName.INSURED_VIEW,
			errorMessages: ['One or more questions in the sections below require your attention.  Please review and correct your answers.'],
		});
	}
);

// Get Endorsement Mortgagees View Section Selector.
const makeGetEndorsementMortgageesViewSectionSelector = () => createSelector(
	[
		makeGetEndorsementMortgageesRelatedSectionsSelector(),
		makeIsEndorsementInTheWorksSelector(),
	],
	(
		mortgageesRelatedSections: EndorsementSection[],
		isEndorsementInTheWorks: boolean,
	): EndorsementSection => {
		let doMortgageesRelatedSectionsHaveErrors: boolean = false;
		let areAnyMortgageeRelatedSectionsEditable: boolean = true;

		if (mortgageesRelatedSections.length > 0) {
			doMortgageesRelatedSectionsHaveErrors = mortgageesRelatedSections.some((mortgageeRelatedSection: EndorsementSection) => mortgageeRelatedSection.status?.statusCode === SectionStatus.REQUIRED);
			areAnyMortgageeRelatedSectionsEditable = mortgageesRelatedSections.some((mortgageeRelatedSection: EndorsementSection) => mortgageeRelatedSection.isEditable);
		}
		// Create section.
		return createEndorsementSection({
			hasErrors: doMortgageesRelatedSectionsHaveErrors,
			isEditable: areAnyMortgageeRelatedSectionsEditable && isEndorsementInTheWorks,
			friendlyName: 'Mortgagees',
			sectionName: EndorsementSectionName.MORTGAGEES_VIEW,
			errorMessages: ['One or more questions in the sections below require your attention.  Please review and correct your answers.'],
		});
	}
);

// Get Endorsement Landing Section Selector.
const makeGetEndorsementLandingSectionSelector = () => createSelector(
	[
		makeGetEndorsementLandingRelatedSectionsSelector(),
		makeIsEndorsementInTheWorksSelector(),
		makeIsEndorsementDateValidSelector()
	],
	(
		landingRelatedSections: EndorsementSection[],
		isEndorsementInTheWorks: boolean,
		isEndorsementDateValid: boolean,
	): EndorsementSection => {
		let doLandingRelatedSectionsHaveErrors: boolean = false;
		let areAnyLandingRelatedSectionsEditable: boolean = true;

		if (landingRelatedSections.length > 0) {
			doLandingRelatedSectionsHaveErrors = landingRelatedSections.some((landingRelatedSection: EndorsementSection) => landingRelatedSection.status?.statusCode === SectionStatus.REQUIRED);
			areAnyLandingRelatedSectionsEditable = landingRelatedSections.some((landingRelatedSection: EndorsementSection) => landingRelatedSection.isEditable);
		}

		// Create section.
		return createEndorsementSection({
			hasErrors: !isEndorsementDateValid || doLandingRelatedSectionsHaveErrors,
			isEditable: areAnyLandingRelatedSectionsEditable && isEndorsementInTheWorks,
			friendlyName: 'Landing',
			sectionName: EndorsementSectionName.LANDING,
			errorMessages: ['One or more questions in the sections below require your attention.  Please review and correct your answers.'],
		});
	}
);

// Get Endorsement Review Section Selector.
const makeGetEndorsementReviewSectionSelector = () => createSelector(
	[makeIsEndorsementInTheWorksSelector()],
	(isEndorsementInTheWorks: boolean): EndorsementSection => {
		// Setup the section status info.
		let endorsementSectionStatusInfo: SectionStatusInfo = new SectionStatusInfo();
		endorsementSectionStatusInfo.statusCode = isEndorsementInTheWorks ? SectionStatus.NOT_ELIGIBLE : SectionStatus.COMPLETE;
		endorsementSectionStatusInfo.statusMessage = isEndorsementInTheWorks ? 'This page cannot be view until the endorsement is submitted or canceled.' : String.empty();

		// Set up the section data.
		let reviewSection: EndorsementSection = new EndorsementSection();
		reviewSection.friendlyName = 'Review';
		reviewSection.isEditable = false;
		reviewSection.name = EndorsementSectionName.REVIEW;
		reviewSection.status = endorsementSectionStatusInfo;

		return reviewSection;
	}
);

// Get Endorsement Add Building Section Selector.
const makeGetEndorsementAddBuildingSectionSelector = () => createSelector(
	[
		makeIsEndorsementInTheWorksSelector(),
		makeAreOneOrMoreEndorsementBuildingEntityFieldsDisallowedSelector(),
		makeCanEndorsementAddBuildingsSelector(),
	],
	(
		isEndorsementInTheWorks: boolean,
		areOneOrMoreEndorsementBuildingEntityFieldsDisallowed: boolean,
		canEndorsementAddBuildings: boolean,
	): EndorsementSection => {
		// Create section.
		return createEndorsementSection({
			hasErrors: false,
			isEditable: isEndorsementInTheWorks
				&& !areOneOrMoreEndorsementBuildingEntityFieldsDisallowed
				&& canEndorsementAddBuildings,
			friendlyName: 'Add Building',
			sectionName: EndorsementSectionName.ADD_BUILDING,
		});
	}
);

// Get Endorsement Add Mortgagee Section Selector.
const makeGetEndorsementAddMortgageeSectionSelector = () => createSelector(
	[
		makeIsEndorsementInTheWorksSelector(),
		makeAreOneOrMoreEndorsementMortgageeEntityFieldsDisallowedSelector(),
	],
	(
		isEndorsementInTheWorks: boolean,
		areOneOrMoreEndorsementMortgageeEntityFieldsDisallowed: boolean,
	): EndorsementSection => {
		// Create section.
		return createEndorsementSection({
			hasErrors: false,
			isEditable: isEndorsementInTheWorks
				&& !areOneOrMoreEndorsementMortgageeEntityFieldsDisallowed,
			friendlyName: 'Add Mortgagee',
			sectionName: EndorsementSectionName.ADD_MORTGAGEE,
		});
	}
);

// Get Endorsement Coverages Related Sections Selector.
const makeGetEndorsementCoveragesRelatedSectionsSelector = () => createSelector(
	[
		makeGetEndorsementBuildingSectionsSelector(),
		makeGetEndorsementDwellingPropertyAddressSectionSelector(),
		makeGetEndorsementDwellingPropertyDetailsSectionSelector(),
		makeGetEndorsementDwellingLimitsSectionSelector(),
		makeGetEndorsementPerilsAndDeductiblesSectionSelector(),
	],
	(
		buildingSections: EndorsementSection[],
		propertyAddressSection: EndorsementSection,
		dwellingSection: EndorsementSection,
		limitsSection: EndorsementSection,
		perilsAndDeductiblesSection: EndorsementSection,
	): EndorsementSection[] => {
		return [
			...buildingSections,
			propertyAddressSection,
			dwellingSection,
			limitsSection,
			perilsAndDeductiblesSection,
		];
	}
);

// Get Endorsement Insured Related Sections Selector.
const makeGetEndorsementInsuredRelatedSectionsSelector = () => createSelector(
	[makeGetEndorsementInsuredSectionSelector()],
	(insuredSection: EndorsementSection): EndorsementSection[] => {
		return [insuredSection];
	}
);

// Get Endorsement Mortgagees Related Sections Selector.
const makeGetEndorsementMortgageesRelatedSectionsSelector = () => createSelector(
	[makeGetEndorsementMortgageeSectionsSelector()],
	(mortgageeSections: EndorsementSection[]): EndorsementSection[] => {
		return [...mortgageeSections];
	}
);

// Get Endorsement Landing Related Sections Selector.
const makeGetEndorsementLandingRelatedSectionsSelector = () => createSelector(
	[
		makeGetEndorsementCoveragesViewSectionSelector(),
		makeGetEndorsementInsuredViewSectionSelector(),
		makeGetEndorsementMortgageesViewSectionSelector(),
	],
	(
		coveragesViewSection: EndorsementSection,
		insuredViewSection: EndorsementSection,
		mortgageesViewSection: EndorsementSection,
	): EndorsementSection[] => {
		return [
			coveragesViewSection,
			insuredViewSection,
			mortgageesViewSection,
		];
	}
);

// Get Endorsement Sections Selector.
const makeGetEndorsementSectionsSelector = () => createSelector(
	[
		makeGetEndorsementCoveragesRelatedSectionsSelector(),
		makeGetEndorsementInsuredRelatedSectionsSelector(),
		makeGetEndorsementMortgageesRelatedSectionsSelector(),
		makeGetEndorsementLandingRelatedSectionsSelector(),
		makeGetEndorsementLandingSectionSelector(),
		makeGetEndorsementReviewSectionSelector(),
		makeGetEndorsementAddBuildingSectionSelector(),
		makeGetEndorsementAddMortgageeSectionSelector(),
	],
	(
		coveragesRelatedSections: EndorsementSection[],
		insuredRelatedSections: EndorsementSection[],
		mortgageesRelatedSections: EndorsementSection[],
		landingRelatedSections: EndorsementSection[],
		landingSection: EndorsementSection,
		reviewSection: EndorsementSection,
		addBuildingSection: EndorsementSection,
		addMortgageeSection: EndorsementSection,
	): EndorsementSection[] => {
		return [
			...coveragesRelatedSections,
			...insuredRelatedSections,
			...mortgageesRelatedSections,
			...landingRelatedSections,
			landingSection,
			reviewSection,
			addBuildingSection,
			addMortgageeSection,
		];
	}
);

// Get Endorsement Redirect Url For Section With Input Error Selector.
const makeGetEndorsementRedirectUrlForSectionWithInputErrorSelector = () => createSelector(
	[makeGetEndorsementSectionsSelector()],
	(endorsementSections: EndorsementSection[]): string | undefined => {
		let requiredEndorsementSection: EndorsementSection | undefined = endorsementSections.find((endorsementSection: EndorsementSection) =>
			endorsementSection.status?.statusCode === SectionStatus.REQUIRED
			&& endorsementSection.name !== EndorsementSectionName.LANDING
			&& endorsementSection.name !== EndorsementSectionName.ADD_BUILDING
			&& endorsementSection.name !== EndorsementSectionName.ADD_MORTGAGEE
			&& endorsementSection.name !== EndorsementSectionName.REVIEW
			&& endorsementSection.name !== EndorsementSectionName.UNKNOWN
		);
		let endorsementRedirectToInputErrorUrl: string | undefined = undefined;

		if (requiredEndorsementSection !== undefined) {
			endorsementRedirectToInputErrorUrl = `${requiredEndorsementSection.name}`;
			if (!String.isNullOrWhiteSpace(requiredEndorsementSection.key)) {
				endorsementRedirectToInputErrorUrl += `/${requiredEndorsementSection.key}`;
			}
		}
		return endorsementRedirectToInputErrorUrl;
	}
);

// Get Endorsement Redirect Url For Section With Premium Bearing Input Error Selector.
const makeGetEndorsementRedirectUrlForSectionWithPremiumBearingInputErrorSelector = () => createSelector(
	[makeGetEndorsementSectionsSelector()],
	(endorsementSections: EndorsementSection[]): string | undefined => {
		let requiredEndorsementSection: EndorsementSection | undefined = endorsementSections.find((endorsementSection: EndorsementSection) =>
			endorsementSection.status?.statusCode === SectionStatus.REQUIRED
			&& (endorsementSection.name === EndorsementSectionName.BUILDING
				|| endorsementSection.name === EndorsementSectionName.DWELLING
				|| endorsementSection.name === EndorsementSectionName.LIMITS
				|| endorsementSection.name === EndorsementSectionName.PERILS_AND_DEDUCTIBLES
				|| endorsementSection.name === EndorsementSectionName.PROPERTY_ADDRESS)
		);
		let endorsementRedirectToInputErrorUrl: string | undefined = undefined;

		if (requiredEndorsementSection !== undefined) {
			endorsementRedirectToInputErrorUrl = `${requiredEndorsementSection.name}`;
			if (!String.isNullOrWhiteSpace(requiredEndorsementSection.key)) {
				endorsementRedirectToInputErrorUrl += `/${requiredEndorsementSection.key}`;
			}
		}
		return endorsementRedirectToInputErrorUrl;
	}
);

// Is Endorsement Dwelling Property Address Section Complete Selector.
const makeIsEndorsementDwellingPropertyAddressSectionCompleteSelector = () => createSelector(
	[makeGetEndorsementDwellingPropertyAddressSectionSelector()],
	(propertyAddressSection: EndorsementSection): boolean =>
		propertyAddressSection.status?.statusCode === SectionStatus.COMPLETE,
);

// Is Endorsement Dwelling Limits Section Complete Selector.
const makeIsEndorsementDwellingLimitsSectionCompleteSelector = () => createSelector(
	[makeGetEndorsementDwellingLimitsSectionSelector()],
	(limitsSection: EndorsementSection): boolean =>
		limitsSection.status?.statusCode === SectionStatus.COMPLETE,
);

// Is Endorsement Coverages Section Complete Selector.
const makeIsEndorsementCoveragesSectionCompleteSelector = () => createSelector(
	[makeGetEndorsementCoveragesViewSectionSelector()],
	(coveragesViewSection: EndorsementSection): boolean => 
		coveragesViewSection.status?.statusCode === SectionStatus.COMPLETE,
);

// Is Endorsement Insured Information Section Complete Selector.
const makeIsEndorsementInsuredInformationSectionCompleteSelector = () => createSelector(
	[makeGetEndorsementInsuredViewSectionSelector()],
	(insuredViewSection: EndorsementSection): boolean =>
		insuredViewSection.status?.statusCode === SectionStatus.COMPLETE,

);

// Is Endorsement Mortgagees Section Complete Selector.
const makeIsEndorsementMortgageesSectionCompleteSelector = () => createSelector(
	[makeGetEndorsementMortgageesViewSectionSelector()],
	(mortgageesViewSection: EndorsementSection): boolean => 
		mortgageesViewSection.status?.statusCode === SectionStatus.COMPLETE,
	
);

// Is Endorsement Perils And Deductibles Section Complete Selector.
const makeIsEndorsementPerilsAndDeductiblesSectionCompleteSelector = () => createSelector(
	[makeGetEndorsementPerilsAndDeductiblesSectionSelector()],
	(perilsAndDeductiblesSection: EndorsementSection): boolean => 
		perilsAndDeductiblesSection.status?.statusCode === SectionStatus.COMPLETE,
	
);

// Are All Endorsement Buildings Complete Selector.
const makeAreAllEndorsementBuildingsCompleteSelector = () => createSelector(
	[makeGetEndorsementBuildingSectionsSelector()],
	(buildingSections: EndorsementSection[]): boolean =>
		buildingSections.every((buildingSection: EndorsementSection) => buildingSection.status?.statusCode === SectionStatus.COMPLETE)

);

// Is Endorsement Section Complete Selector.
const makeIsEndorsementSectionCompleteSelector = () => createSelector(
	[
		getEndorsementSectionName,
		getOptionalEndorsementSectionKey,
		makeGetEndorsementSectionsSelector()
	],
	(
		endorsementSectionName: EndorsementSectionName,
		endorsementSectionKey: string | undefined,
		endorsementSections: EndorsementSection[]
	): boolean => {
		let matchingEndorsementSection: EndorsementSection | undefined = endorsementSections.find((endorsementSection: EndorsementSection) => {
			return endorsementSection.name === endorsementSectionName
				&& endorsementSection.status?.statusCode === SectionStatus.COMPLETE
				&& ((endorsementSectionKey === undefined && endorsementSection.key === null)
					|| endorsementSection.key === endorsementSectionKey);
		});

		return matchingEndorsementSection !== undefined;
	}
);

// Is Endorsement Section Accessible Selector.
const makeIsEndorsementSectionAccessibleSelector = () => createSelector(
	[
		getEndorsementSectionName,
		getOptionalEndorsementSectionKey,
		makeGetEndorsementSectionsSelector()
	],
	(
		endorsementSectionName: EndorsementSectionName,
		endorsementSectionKey: string | undefined,
		endorsementSections: EndorsementSection[]
	): boolean => {
		let matchingEndorsementSection: EndorsementSection | undefined = endorsementSections.find((endorsementSection: EndorsementSection) => {
			return endorsementSection.name === endorsementSectionName
				&& endorsementSection.status?.statusCode !== SectionStatus.NOT_ELIGIBLE
				&& ((endorsementSectionKey === undefined && endorsementSection.key === null)
					|| endorsementSection.key === endorsementSectionKey);
		});

		return matchingEndorsementSection !== undefined;
	}
);

// Is Endorsement Section Editable Selector.
const makeIsEndorsementSectionEditableSelector = () => createSelector(
	[
		getEndorsementSectionName,
		getOptionalEndorsementSectionKey,
		makeGetEndorsementSectionsSelector()
	],
	(
		endorsementSectionName: EndorsementSectionName,
		endorsementSectionKey: string | undefined,
		endorsementSections: EndorsementSection[]
	): boolean => {
		let matchingEndorsementSection: EndorsementSection | undefined = endorsementSections.find((endorsementSection: EndorsementSection) => {
			return endorsementSection.name === endorsementSectionName
				&& endorsementSection.isEditable
				&& ((endorsementSectionKey === undefined && endorsementSection.key === null)
					|| endorsementSection.key === endorsementSectionKey);
		});

		return matchingEndorsementSection !== undefined;
	}
);

// Get Endorsement Section Status Message Selector.
const makeGetEndorsementSectionStatusMessageSelector = () => createSelector(
	[
		getEndorsementSectionName,
		getOptionalEndorsementSectionKey,
		makeGetEndorsementSectionsSelector()
	],
	(
		endorsementSectionName: EndorsementSectionName,
		endorsementSectionKey: string | undefined,
		endorsementSections: EndorsementSection[]
	): string | undefined => {
		let matchingEndorsementSection: EndorsementSection | undefined = endorsementSections.find((endorsementSection: EndorsementSection) => {
			return endorsementSection.name === endorsementSectionName
				&& ((endorsementSectionKey === undefined && endorsementSection.key === null)
					|| endorsementSection.key === endorsementSectionKey);
		});

		return matchingEndorsementSection?.status?.statusMessage ?? undefined;
	}
);

// Export composed methods.
export {
	getEndorsementSectionName,
	getOptionalEndorsementSectionKey,
};

// export selectors.
export {
	makeGetEndorsementBuildingSectionsSelector,
	makeGetEndorsementMortgageeSectionsSelector,
	makeGetEndorsementDwellingPropertyAddressSectionSelector,
	makeGetEndorsementDwellingPropertyDetailsSectionSelector,
	makeGetEndorsementDwellingLimitsSectionSelector,
	makeGetEndorsementPerilsAndDeductiblesSectionSelector,
	makeGetEndorsementInsuredSectionSelector,
	makeGetEndorsementCoveragesViewSectionSelector,
	makeGetEndorsementInsuredViewSectionSelector,
	makeGetEndorsementMortgageesViewSectionSelector,
	makeGetEndorsementLandingSectionSelector,
	makeGetEndorsementReviewSectionSelector,
	makeGetEndorsementAddBuildingSectionSelector,
	makeGetEndorsementAddMortgageeSectionSelector,
	makeGetEndorsementCoveragesRelatedSectionsSelector,
	makeGetEndorsementInsuredRelatedSectionsSelector,
	makeGetEndorsementMortgageesRelatedSectionsSelector,
	makeGetEndorsementLandingRelatedSectionsSelector,
	makeGetEndorsementSectionsSelector,
	makeGetEndorsementRedirectUrlForSectionWithInputErrorSelector,
	makeGetEndorsementRedirectUrlForSectionWithPremiumBearingInputErrorSelector,
	makeIsEndorsementDwellingPropertyAddressSectionCompleteSelector,
	makeIsEndorsementDwellingLimitsSectionCompleteSelector,
	makeIsEndorsementCoveragesSectionCompleteSelector,
	makeIsEndorsementInsuredInformationSectionCompleteSelector,
	makeIsEndorsementMortgageesSectionCompleteSelector,
	makeIsEndorsementPerilsAndDeductiblesSectionCompleteSelector,
	makeAreAllEndorsementBuildingsCompleteSelector,
	makeIsEndorsementSectionCompleteSelector,
	makeIsEndorsementSectionAccessibleSelector,
	makeIsEndorsementSectionEditableSelector,
	makeGetEndorsementSectionStatusMessageSelector,
};